import { ArrowUpRight, CircleCheckBig, Code2, Compass, Search, TrendingUp } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { PROCESS } from "@/config/content";

const STEP_META = [
  { icon: Search, outcome: "Problema compreendido" },
  { icon: Compass, outcome: "Direção compartilhada" },
  { icon: Code2, outcome: "Produto visível" },
  { icon: CircleCheckBig, outcome: "Experiência validada" },
  { icon: TrendingUp, outcome: "Base pronta para crescer" },
];

export const Process = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start center", "end center"],
  });
  const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section ref={ref} id="processo" data-header-theme="light" className="bg-[#dfe9ff] py-24 text-[#0c1530] md:py-36" data-testid="process-section">
      <div className="site-shell grid gap-16 lg:grid-cols-[.72fr_1.28fr] lg:gap-20">
        <aside className="lg:sticky lg:top-28 lg:h-fit">
          <p className="eyebrow !text-[#0c1530]/55">Como trabalhamos</p>
          <h2 className="display-title mt-6 max-w-2xl text-4xl leading-[1] sm:text-5xl lg:text-7xl">
            Você acompanha o caminho. Não espera pelo resultado.
          </h2>
          <p className="mt-7 max-w-md text-base leading-relaxed text-[#0c1530]/58">
            Trabalhamos com decisões visíveis, ciclos curtos e proximidade suficiente para manter produto e negócio sempre na mesma direção.
          </p>
          <Link to="/contato" className="group mt-9 inline-flex items-center gap-3 border-b border-[#0c1530]/35 pb-2 text-sm font-bold transition-colors hover:border-[#0c1530]">
            Conversar sobre um projeto
            <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </aside>

        <div className="relative">
          <div className="absolute bottom-0 left-[21px] top-0 w-px bg-[#0c1530]/14 sm:left-[27px]" aria-hidden="true">
            <motion.div className="h-full w-full origin-top bg-[#2851aa]" style={reduce ? { scaleY: 1 } : { scaleY: progressScale }} />
          </div>

          <ol>
            {PROCESS.map((step, index) => {
              const { icon: Icon, outcome } = STEP_META[index];
              return (
                <motion.li
                  key={step.title}
                  initial={reduce ? false : { opacity: 0.28, y: 34 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ amount: 0.48, once: false }}
                  transition={{ duration: 0.58, ease: [0.16, 1, 0.3, 1] }}
                  className="grid grid-cols-[44px_minmax(0,1fr)] gap-4 border-b border-[#0c1530]/15 py-10 first:border-t sm:grid-cols-[56px_minmax(0,1fr)] sm:gap-8 sm:py-12 md:min-h-[34svh] md:items-center md:py-16"
                  data-testid={`process-step-${index}`}
                >
                  <span className="relative z-10 flex h-11 w-11 items-center justify-center border border-[#0c1530]/18 bg-[#dfe9ff] text-[#2851aa] sm:h-14 sm:w-14"><Icon size={20} /></span>
                  <div className="min-w-0">
                    <p className="text-[9px] font-bold uppercase tracking-[.17em] text-[#2851aa]">{outcome}</p>
                    <div className="mt-4 grid gap-4 md:grid-cols-[.72fr_1.28fr] md:items-start md:gap-10">
                      <h3 className="display-title text-2xl leading-tight sm:text-3xl md:text-4xl">{step.title}</h3>
                      <p className="max-w-xl text-sm leading-relaxed text-[#0c1530]/56 md:text-base">{step.text}</p>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};
